import React from 'react';
import { termUnitLabel } from '../../lib/termDates';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const EditDealDialog = ({
  open,
  onOpenChange,
  deal,
  editDealForm,
  onFieldChange,
  onSave,
  saving
}) => {
  if (!deal) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Deal #{deal?.deal_id}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 py-4">
          {/* Deal Identification */}
          <div className="space-y-2">
            <Label htmlFor="edit_deal_id">Deal ID</Label>
            <Input
              id="edit_deal_id"
              value={editDealForm.deal_id || ''}
              onChange={(e) => onFieldChange('deal_id', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit_deal_shortcode">Shortcode</Label>
            <Input
              id="edit_deal_shortcode"
              value={editDealForm.deal_shortcode || ''}
              onChange={(e) => onFieldChange('deal_shortcode', e.target.value)}
            />
          </div>
          <div className="col-span-2 space-y-2">
            <Label htmlFor="edit_deal_name">Deal Name</Label>
            <Input
              id="edit_deal_name"
              value={editDealForm.deal_name || ''}
              onChange={(e) => onFieldChange('deal_name', e.target.value)}
            />
          </div>
          
          {/* Financial Details */}
          <div className="space-y-2">
            <Label htmlFor="edit_funding_date">Funding Date</Label>
            <Input
              id="edit_funding_date"
              type="date"
              value={editDealForm.funding_date || ''}
              onChange={(e) => onFieldChange('funding_date', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit_funding_amount">Funding Amount</Label>
            <Input
              id="edit_funding_amount"
              type="number"
              step="0.01"
              value={editDealForm.funding_amount || ''}
              onChange={(e) => onFieldChange('funding_amount', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit_rate">Rate (Factor)</Label>
            <Input
              id="edit_rate"
              type="number"
              step="0.01"
              value={editDealForm.rate || ''}
              onChange={(e) => onFieldChange('rate', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit_term_days">Term ({termUnitLabel(editDealForm.payment_frequency)})</Label>
            <Input
              id="edit_term_days"
              type="number"
              value={editDealForm.term_days || ''}
              onChange={(e) => onFieldChange('term_days', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            {/* Payment Frequency right after Term: the term is counted in this unit (Josh 10/01). Bound to the
                canonical payment_frequency (the old `weekly` boolean is gone from the model). */}
            <Label htmlFor="edit_payment_frequency">Payment Frequency</Label>
            <Select value={editDealForm.payment_frequency || 'Daily'} onValueChange={(v) => onFieldChange('payment_frequency', v)}>
              <SelectTrigger id="edit_payment_frequency" data-testid="edit-payment-frequency"><SelectValue /></SelectTrigger>
              <SelectContent>
                {['Daily', 'Weekly', 'Bi-Weekly', 'Monthly'].map(f => (
                  <SelectItem key={f} value={f}>{f}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit_payback">Payback Amount</Label>
            <Input
              id="edit_payback"
              type="number"
              step="0.01"
              value={editDealForm.payback || ''}
              onChange={(e) => onFieldChange('payback', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit_payment_amount">Payment Amount</Label>
            <Input
              id="edit_payment_amount"
              type="number"
              step="0.01"
              value={editDealForm.payment_amount || ''}
              onChange={(e) => onFieldChange('payment_amount', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit_fees">Fees</Label>
            <Input
              id="edit_fees"
              type="number"
              step="0.01"
              value={editDealForm.fees || ''}
              onChange={(e) => onFieldChange('fees', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit_refi_balance">Refi Balance Payoff</Label>
            <Input
              id="edit_refi_balance"
              type="number"
              step="0.01"
              value={editDealForm.refi_balance_payoff || ''}
              onChange={(e) => onFieldChange('refi_balance_payoff', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit_funder_pct">Funder Percentage</Label>
            <Input
              id="edit_funder_pct"
              type="number"
              step="0.01"
              value={editDealForm.funder_percentage || ''}
              onChange={(e) => onFieldChange('funder_percentage', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit_commission_rate">Commission Rate (%)</Label>
            <Input
              id="edit_commission_rate"
              type="number"
              step="0.01"
              value={editDealForm.commission_rate || ''}
              onChange={(e) => onFieldChange('commission_rate', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit_broker_split">Broker Split Rate (%)</Label>
            <Input
              id="edit_broker_split"
              type="number"
              step="0.01"
              value={editDealForm.broker_split_rate || ''}
              onChange={(e) => onFieldChange('broker_split_rate', e.target.value)}
            />
          </div>
        </div>

        {/* ─── Performance Tracking section (Feb 2026 Part A) ─── */}
        <div className="border-t border-slate-200 pt-4 mt-2">
          <h3 className="text-sm font-semibold text-slate-900">Performance Tracking</h3>
          <p className="text-xs text-slate-500 mt-1 mb-3">
            These fields resolve Summary tab warnings and appear in the Data Health worklist when blank.
          </p>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="edit_commission_rate">Commission Rate (%)</Label>
              <Input
                id="edit_commission_rate"
                data-testid="edit-deal-commission-rate-input"
                type="number"
                step="0.01"
                placeholder="Leave blank if unknown"
                value={editDealForm.commission_rate ?? ''}
                onChange={(e) => onFieldChange('commission_rate', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit_broker_commission">Scheduled Commission ($)</Label>
              <Input
                id="edit_broker_commission"
                data-testid="edit-deal-broker-commission-input"
                type="number"
                step="0.01"
                placeholder="Leave blank if unknown"
                value={editDealForm.broker_commission ?? ''}
                onChange={(e) => onFieldChange('broker_commission', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit_expected_payoff_date">Expected Payoff Date</Label>
              <Input
                id="edit_expected_payoff_date"
                data-testid="edit-deal-expected-payoff-date-input"
                type="date"
                value={editDealForm.estimated_end_date || ''}
                onChange={(e) => onFieldChange('estimated_end_date', e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditDealDialog;
